import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ClipboardList, CheckCircle2, Clock, Compass, ArrowLeft, Loader2, BookOpenCheck, Award, Building2, UserRound, CalendarDays } from "lucide-react";

const STAT_ITEMS = [
  { key: "my_applications", label: "طلباتي", icon: ClipboardList, color: "bg-teal-50 text-teal-700" },
  { key: "accepted", label: "طلبات مقبولة", icon: CheckCircle2, color: "bg-emerald-50 text-emerald-700" },
  { key: "pending", label: "قيد الانتظار", icon: Clock, color: "bg-amber-50 text-amber-700" },
  { key: "opportunities_available", label: "فرص متاحة", icon: Compass, color: "bg-sky-50 text-sky-700" },
];

export default function StudentHome() {
  const { user } = useAuth();
  const female = ["female", "أنثى"].includes(user?.gender);
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [courses, setCourses] = useState([]);
  const [certs, setCerts] = useState([]);
  const [training, setTraining] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setError("");
      const results = await Promise.allSettled([
        api.get("/dashboard/stats"), api.get("/applications/mine"),
        api.get("/my-courses"), api.get("/certificates/mine"),
        api.get("/student/training-current"),
      ]);
      const [s, a, c, ce, tr] = results;
      if (s.status === "fulfilled") setStats(s.value.data.stats);
      else { setStats({}); setError("تعذر تحميل ملخص لوحة الطالب. حاول مرة أخرى."); }
      if (a.status === "fulfilled") setApps(a.value.data.applications || []);
      if (c.status === "fulfilled") setCourses(c.value.data.enrollments || []);
      if (ce.status === "fulfilled") setCerts(ce.value.data.certificates || []);
      if (tr.status === "fulfilled") setTraining(tr.value.data.training || null);
    })();
  }, []);

  return (
    <div data-testid="student-home">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-widest text-teal-700">لوحتي</div>
        <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">مرحباً {user?.name} 👋</h1>
        <p className="mt-2 text-slate-600">تابع رحلتك التدريبية واستكشف فرصاً جديدة.</p>
      </div>

      {error && <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" role="alert">{error}<button type="button" onClick={() => window.location.reload()} className="ms-3 font-bold underline">إعادة المحاولة</button></div>}
      {!stats ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {STAT_ITEMS.map((s) => (
              <div key={s.key} className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid={`stat-${s.key}`}>
                <div className={`h-11 w-11 rounded-xl ${s.color} grid place-items-center mb-4`}>
                  <s.icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <div className="text-3xl font-black text-slate-900 arabic-numerals">{stats[s.key] ?? 0}</div>
                <div className="text-sm text-slate-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          {training && (
            <section className="mb-8 p-4 rounded-2xl bg-white border border-teal-200 card-shadow" data-testid="current-training-card">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-5">
                <div><div className="text-xs font-bold text-teal-700 mb-2">تدريبي الحالي</div>
                  <h2 className="text-xl font-heading font-black text-slate-900">{training.opportunity?.title || training.application?.opportunity_title}</h2>
                  <div className="mt-3 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-slate-600">
                    <span className="inline-flex items-center gap-2"><Building2 className="h-4 w-4 text-teal-700" />{training.association?.organization_name || training.association?.name || training.application?.assigned_association_name}</span>
                    {training.mentor && <span className="inline-flex items-center gap-2"><UserRound className="h-4 w-4 text-teal-700" />الموجّه: {training.mentor.name}</span>}
                    {(training.opportunity?.training_start || training.opportunity?.training_end) && <span className="inline-flex items-center gap-2"><CalendarDays className="h-4 w-4 text-teal-700" />{training.opportunity?.training_start ? new Date(training.opportunity.training_start).toLocaleDateString("ar-SA") : "—"} — {training.opportunity?.training_end ? new Date(training.opportunity.training_end).toLocaleDateString("ar-SA") : "—"}</span>}
                  </div>
                </div>
                <Link to={`/dashboard/journeys/${training.application?.id}`} className="inline-flex items-center gap-2 text-sm font-bold text-teal-700">عرض الرحلة <ArrowLeft className="h-4 w-4" /></Link>
              </div>
              <div className="mt-5"><div className="flex justify-between text-xs text-slate-600 mb-2"><span>تقدم إنجاز المهام</span><span>{training.progress?.summary?.progress_percent || 0}%</span></div>
                <progress className="w-full h-2.5 accent-teal-600" max="100" value={training.progress?.summary?.progress_percent || 0} />
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 text-center text-xs">
                  <div className="rounded-xl bg-slate-50 p-3"><b className="block text-lg text-slate-900">{training.progress?.summary?.total || 0}</b>المهام</div>
                  <div className="rounded-xl bg-emerald-50 p-3"><b className="block text-lg text-emerald-700">{training.progress?.summary?.done || 0}</b>مكتملة</div>
                  <div className="rounded-xl bg-teal-50 p-3"><b className="block text-lg text-teal-700">{training.progress?.summary?.task_hours || 0}</b>ساعات الإنجاز</div>
                  <div className="rounded-xl bg-sky-50 p-3"><b className="block text-lg text-sky-700">{training.progress?.summary?.present_days || 0}</b>أيام حضور</div>
                  <div className="rounded-xl bg-rose-50 p-3"><b className="block text-lg text-rose-700">{training.progress?.summary?.absent_days || 0}</b>أيام غياب</div>
                </div>
              </div>
            </section>
          )}

          <div className="grid md:grid-cols-4 gap-4">
            <Link to="/dashboard/browse-opportunities" className="group p-4 rounded-2xl bg-gradient-to-br from-teal-700 to-teal-800 text-white hover:-translate-y-1 transition-transform">
              <Compass className="h-8 w-8 mb-3" strokeWidth={1.5} />
              <div className="text-lg font-heading font-bold">استكشف الفرص</div>
              <div className="mt-1 text-teal-100 text-xs">تصفّح البرامج وقدّم.</div>
            </Link>
            <Link to="/dashboard/my-courses" className="group p-4 rounded-2xl bg-white border border-cream-200 hover:border-teal-200 hover:card-shadow-lg transition-all">
              <BookOpenCheck className="h-8 w-8 text-teal-700 mb-3" strokeWidth={1.5} />
              <div className="text-lg font-heading font-bold text-slate-900">برنامج الإعداد والتهيئة</div>
              <div className="mt-1 text-slate-600 text-xs">تابع التعلّم.</div>
            </Link>
            <Link to="/dashboard/tasks" className="p-4 rounded-2xl bg-white border border-cream-200 hover:card-shadow-lg transition-all">
              <ClipboardList className="h-8 w-8 text-teal-700 mb-3" strokeWidth={1.5} />
              <div className="text-lg font-heading font-bold text-slate-900">مهامي</div>
              <div className="mt-1 text-slate-600 text-xs">التسليمات والتقييم.</div>
            </Link>
            <Link to="/dashboard/certificates" className="p-4 rounded-2xl bg-white border border-cream-200 hover:card-shadow-lg transition-all">
              <Award className="h-8 w-8 text-teal-700 mb-3" strokeWidth={1.5} />
              <div className="text-lg font-heading font-bold text-slate-900">شهاداتي</div>
              <div className="mt-1 text-slate-600 text-xs">شهاداتك المُصدرة.</div>
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
